import React, { useState, useEffect } from 'react';
import { Sliders, RotateCcw, Eye, EyeOff, Search } from 'lucide-react';
import { 
  getFiltersByCategory, 
  getFilterById, 
  applyFilterToStyle, 
  isFilterAdjustable, 
  getFilterRange 
} from '../../utils/imageFilters';

const FilterPanel = ({ 
  selectedElement, 
  template_content, 
  settemplate_content, 
  onClose 
}) => {
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [filterIntensity, setFilterIntensity] = useState(1);
  const [showPreview, setShowPreview] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState('Basic');
  const [searchTerm, setSearchTerm] = useState('');

  const filtersByCategory = getFiltersByCategory();

  // Initialize filter state from selected element
  useEffect(() => {
    if (selectedElement?.filters) {
      setSelectedFilter(selectedElement.filters.type || 'none');
      setFilterIntensity(selectedElement.filters.intensity || 1);
    } else {
      setSelectedFilter('none');
      setFilterIntensity(1);
    }
  }, [selectedElement]);

  const applyFilter = (filterId, intensity = null) => {
    if (!selectedElement) return;

    const updatedTemplate = { ...template_content };
    const pages = [...updatedTemplate.pages];
    const currentPageIndex = 0; // Assuming single page for now
    const page = { ...pages[currentPageIndex] };
    const items = [...page.items];

    // Find and update the selected element
    const elementIndex = items.findIndex(item => item.id === selectedElement.id);
    if (elementIndex !== -1) {
      const updatedElement = { ...items[elementIndex] };
      
      if (filterId === 'none') {
        // Remove filters
        delete updatedElement.filters;
      } else {
        // Apply filter
        updatedElement.filters = {
          type: filterId,
          intensity: intensity || filterIntensity,
          cssFilter: applyFilterToStyle(filterId, intensity || filterIntensity)
        };
      }

      items[elementIndex] = updatedElement;
      page.items = items;
      pages[currentPageIndex] = page;
      updatedTemplate.pages = pages;

      settemplate_content(updatedTemplate);
    }
  };

  const handleFilterSelect = (filterId) => {
    setSelectedFilter(filterId);
    const filter = getFilterById(filterId);
    
    if (filter.adjustable && filter.range) {
      setFilterIntensity(filter.range.default);
      applyFilter(filterId, filter.range.default);
    } else {
      applyFilter(filterId);
    }
  };

  const handleIntensityChange = (value) => {
    setFilterIntensity(value);
    applyFilter(selectedFilter, value);
  };

  const resetFilter = () => {
    setSelectedFilter('none');
    setFilterIntensity(1);
    applyFilter('none');
  };

  const toggleCategory = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  if (!selectedElement || (selectedElement.type !== 'image' && selectedElement.type !== 'IMAGE')) {
    return (
      <div className="p-4 text-center text-gray-500">
        <div className="mb-4">
          <Sliders className="w-12 h-12 mx-auto text-gray-300" />
        </div>
        <p>Select an image to apply filters</p>
      </div>
    );
  }

  const currentFilter = getFilterById(selectedFilter);
  const isAdjustable = isFilterAdjustable(selectedFilter);
  const filterRange = getFilterRange(selectedFilter);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Image Filters</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className={`p-2 rounded-lg transition-colors ${
                showPreview 
                  ? 'bg-blue-100 text-blue-600' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={showPreview ? 'Hide Preview' : 'Show Preview'}
            >
              {showPreview ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
            <button
              onClick={resetFilter}
              className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              title="Reset Filter"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search filters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Current Filter Info */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-lg">{currentFilter.icon}</span>
            <span className="font-medium text-gray-900">{currentFilter.name}</span>
          </div>
          <p className="text-sm text-gray-600">{currentFilter.description}</p>
        </div>
      </div>

      {/* Filter Controls */}
      {isAdjustable && filterRange && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Intensity: {filterIntensity}{filterRange.unit || ''}
          </label>
          <input
            type="range"
            min={filterRange.min}
            max={filterRange.max}
            step={filterRange.step}
            value={filterIntensity}
            onChange={(e) => handleIntensityChange(parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            style={{
              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((filterIntensity - filterRange.min) / (filterRange.max - filterRange.min)) * 100}%, #e5e7eb ${((filterIntensity - filterRange.min) / (filterRange.max - filterRange.min)) * 100}%, #e5e7eb 100%)`
            }}
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{filterRange.min}{filterRange.unit || ''}</span>
            <span>{filterRange.max}{filterRange.unit || ''}</span>
          </div>
        </div>
      )}

      {/* Filter Categories */}
      <div className="flex-1 overflow-y-auto">
        {Object.entries(filtersByCategory).map(([category, filters]) => {
          // Filter based on search term
          const filteredFilters = filters.filter(filter =>
            filter.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            filter.description.toLowerCase().includes(searchTerm.toLowerCase())
          );

          // Skip category if no filters match search
          if (searchTerm && filteredFilters.length === 0) {
            return null;
          }

          const filtersToShow = searchTerm ? filteredFilters : filters;

          return (
          <div key={category} className="border-b border-gray-200">
            <button
              onClick={() => toggleCategory(category)}
              className="w-full px-4 py-3 text-left font-medium text-gray-900 hover:bg-gray-50 flex items-center justify-between"
            >
              <span>{category} ({filtersToShow.length})</span>
              <span className={`transform transition-transform ${
                expandedCategory === category || searchTerm ? 'rotate-90' : ''
              }`}>
                ▶
              </span>
            </button>

            {(expandedCategory === category || searchTerm) && (
              <div className="grid grid-cols-2 gap-2 p-4">
                {filtersToShow.map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => handleFilterSelect(filter.id)}
                    className={`p-3 rounded-lg border-2 transition-all hover:shadow-md group ${
                      selectedFilter === filter.id
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-2xl mb-1 group-hover:scale-110 transition-transform">
                        {filter.icon}
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        {filter.name}
                      </div>
                      {selectedFilter === filter.id && (
                        <div className="mt-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mx-auto"></div>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
        })}
      </div>

      {/* Preview Toggle */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Live Preview</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showPreview}
              onChange={(e) => setShowPreview(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
